// JobStatus：轮询 /api/jobs/{id} 显示 cache-policy batch-apply 进度。
// 5s 轮询，job.status === "completed" 时停止；同时 SSE batch_apply 事件
// 可以触发 query invalidate（由调用方注入）。

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { apiGet } from "@/api/client";
import { jobPath } from "@/api/endpoints";
import type { JobState } from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { StatusBadge } from "@/components/ui/StatusBadge";

export interface JobStatusProps {
  jobID: string;
  // 完成后回调（如刷新外层 query）。
  onCompleted?: (snapshot: JobState) => void;
}

export function JobStatus({ jobID, onCompleted }: JobStatusProps) {
  const completedRef = useRef<string | null>(null);
  const q = useQuery({
    queryKey: ["job", jobID],
    queryFn: () => apiGet<JobState>(jobPath(jobID)),
    refetchInterval: (query) =>
      query.state.data?.status === "completed" ? false : 5_000,
  });

  useEffect(() => {
    if (q.data?.status === "completed" && completedRef.current !== q.data.id) {
      completedRef.current = q.data.id;
      onCompleted?.(q.data);
    }
  }, [onCompleted, q.data]);

  return (
    <QueryView query={q}>
      {(job) => (
        <div className="space-y-2 rounded border border-[hsl(var(--border))] p-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-mono">{job.id}</span>
            <StatusBadge variant={job.status === "completed" ? "ok" : "warn"}>
              {job.status}
            </StatusBadge>
            <span className="text-[hsl(var(--muted-foreground))]">
              {job.completed}/{job.total} (ok {job.ok}, fail {job.failed})
            </span>
          </div>
          {job.last_error ? (
            <div className="text-[hsl(var(--danger))]">{job.last_error}</div>
          ) : null}
        </div>
      )}
    </QueryView>
  );
}
